import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Chip, Button, Paper, Alert, CircularProgress, TextField,
    Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import dayjs from 'dayjs';
import { getReferralFees } from '../../api/commissionProgressApi';
import { recordReferralPayout } from '../../api/commissionSlipApi';
import { useUser } from '../../context/userContext';
import LogViewer from '../common/logViewer';

/**
 * Referral Fee detail — the Commission detail page's layout, for a referral.
 * (user 2026-10-14: "the specific page of referral fee can use the commission ... similar
 * page design and style. but make it max width")
 *
 * Same four summary cards, same calculation box, same Sales Information block, same payout
 * sections. Full width rather than the commission page's 700px column, because this page
 * carries the referrer and their bank details alongside the sale and that does not fit in
 * one narrow column.
 *
 * Keyed on the SALE, not on a CommissionProgress row: an Astern-borne fee has no commission
 * row to key on — that is the whole point of bearing it outside the pot — and those are the
 * majority of referrals.
 *
 * The payout fields are READ-ONLY here, which is the one real departure. A commission
 * payment is keyed on the commission record; a referral fee is paid by issuing a referral
 * slip, and approving that slip is what marks it paid. Editable boxes that saved nowhere
 * would be worse than no boxes at all.
 */

const PP = "'Poppins', sans-serif";
const rm = (v) => `RM ${(Math.round((Number(v) || 0) * 100) / 100).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const inputSx = { sx: { fontFamily: PP, fontSize: '0.85rem' } };
const tdSx = { py: 0.75, px: 1.25, fontFamily: PP, fontSize: '0.8rem', color: '#374151' };

function SectionTitle({ children, right }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 2.5, mb: 1, pb: 0.5, borderBottom: '1px solid #E2E8F0' }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: PP }}>
                {children}
            </Typography>
            {right}
        </Box>
    );
}

export default function ReferralFeeDetail() {
    const { salesId } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();
    const [row, setRow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    // Keyed payout. Astern is the payer — this records what they paid, so the fields are
    // typed rather than derived. (user 2026-10-14: "payout still we type. just astern is
    // the payer, we just help them to record too")
    const [payAmount, setPayAmount] = useState('');
    const [payDate, setPayDate] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveErr, setSaveErr] = useState('');
    const [saveOk, setSaveOk] = useState('');

    const fetchDetail = useCallback(async () => {
        try {
            setLoading(true);
            setFailed(false);
            const res = await getReferralFees();
            const list = res?.data || [];
            const hit = list.find(r => String(r?.salesDetailsForCommissionProgress?.salesId) === String(salesId)) || null;
            setRow(hit);
            // Seed the payout boxes from what is recorded, so a re-save edits the figure
            // rather than starting from blank and looking like nothing was ever paid.
            const cp = hit?.commissionProgressDto;
            setPayAmount(cp?.firstPayment != null ? String(cp.firstPayment) : '');
            setPayDate(cp?.firstPaymentOn ? dayjs(cp.firstPaymentOn) : null);
        } catch (e) {
            console.error('Failed to fetch referral fee detail', e);
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [salesId]);

    useEffect(() => { fetchDetail(); }, [fetchDetail]);

    const back = (
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
            sx={{ mb: 2, fontFamily: PP, textTransform: 'none', color: '#64748B', fontWeight: 500, fontSize: '0.85rem' }}>
            Back
        </Button>
    );

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><CircularProgress /></Box>;
    }

    if (!row) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity={failed ? 'error' : 'warning'} sx={{ fontFamily: PP }}>
                    {failed
                        ? `Could not load referral fees. Sale #${salesId} was not checked.`
                        : `Sale #${salesId} has no referral on it. A referral fee exists only where a sale names a referrer.`}
                </Alert>
                <Box sx={{ mt: 2 }}>{back}</Box>
            </Box>
        );
    }

    const sd = row.salesDetailsForCommissionProgress || {};
    const cd = row.commissionProgressDto || {};

    const asternBorne = sd.referralPaidBy !== 'Agent';
    const fee = Number(cd.commPay) || 0;
    const paid = (Number(cd.firstPayment) || 0) + (Number(cd.secondPayment) || 0);
    const remaining = Math.round((fee - paid) * 100) / 100;
    const percentage = fee !== 0 ? Math.round((paid / fee) * 100 * 100) / 100 : 0;
    // The rate shown is the referrer's own rate when we have it, falling back to the one the
    // fee actually implies — so the line always reconciles to the amount beside it.
    const ratePct = sd.referralRatePct != null
        ? Number(sd.referralRatePct)
        : (sd.finalPrice > 0 ? Math.round((fee / sd.finalPrice) * 100 * 100) / 100 : 0);
    const noBank = !sd.referrerBankName && !sd.referrerBankAccountNumber;
    // Everything this referrer has bought from us, newest first.
    const ownSales = sd.referrerCustomerSales || [];
    const ownSalesTotal = ownSales.reduce((s, x) => s + (Number(x.finalPrice) || 0), 0);

    // Only an ASTERN-borne fee is keyed here. An agent-borne one is a real CommissionProgress
    // row paid on the agent's own commission slip — its payment is recorded there, and a
    // second place to type it would be a second answer to the same question.
    const canRecord = asternBorne && !!user?.roleRights?.includes('CommissionSlipGenerator');

    const savePayout = async () => {
        setSaveErr(''); setSaveOk('');
        const amt = parseFloat(payAmount);
        if (!(amt > 0)) { setSaveErr('Enter a payout amount greater than zero.'); return; }
        setSaving(true);
        try {
            await recordReferralPayout({
                salesId: Number(salesId),
                amount: amt,
                paidOn: payDate ? payDate.format('YYYY-MM-DD') : null,
            });
            setSaveOk('Payout recorded.');
            await fetchDetail();
        } catch (e) {
            setSaveErr(e?.response?.data?.message || e?.response?.data || 'Could not save the payout.');
        } finally {
            setSaving(false);
        }
    };

    const slipId = sd.referralSlipId;
    const slipStatus = sd.referralSlipStatus;
    const payStatus = slipStatus === 'Approved' ? 'Paid'
        : slipStatus === 'Pending' ? 'On slip, awaiting approval'
        : cd.paymentStatus || 'Not paid';

    return (
        // Full width, deliberately: the commission page's 700px column cannot hold the sale,
        // the referrer and the bank details side by side. (user 2026-10-14: "make it max width")
        <Box sx={{ width: '100%' }}>
            {back}

            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', mb: 0.5 }}>
                RFC — Sale #{sd.salesId}
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#64748B', fontFamily: PP, mb: 2 }}>
                {sd.customerName} &middot; {sd.referrerName}
                <Chip label={asternBorne ? 'Borne by Astern' : 'Borne by the selling agent'} size="small" sx={{
                    ml: 1, fontSize: '0.6rem', fontWeight: 600,
                    backgroundColor: asternBorne ? '#EFF6FF' : '#FEF3C7',
                    color: asternBorne ? '#0E8FD1' : '#92400E',
                }} />
                <Chip label={payStatus} size="small" sx={{
                    ml: 1, fontSize: '0.6rem', fontWeight: 600,
                    backgroundColor: slipStatus === 'Approved' ? '#D1FAE5' : '#FEF3C7',
                    color: slipStatus === 'Approved' ? '#065F46' : '#92400E',
                }} />
            </Typography>

            <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                {/* Summary cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
                    {[
                        { label: 'RFC', value: rm(fee), bg: '#F8FAFC', color: '#0F172A' },
                        { label: 'Paid', value: rm(paid), bg: '#ECFDF5', color: '#10B981' },
                        { label: 'Remaining', value: rm(remaining), bg: '#FFF7ED', color: remaining > 0 ? '#F59E0B' : '#0F172A' },
                        { label: 'Progress', value: `${percentage}%`, bg: '#EFF6FF', color: '#1A8CFF' },
                    ].map(card => (
                        <Box key={card.label} sx={{ background: card.bg, borderRadius: '10px', padding: '10px 14px' }}>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94A3B8', mb: 0.5, fontFamily: PP }}>
                                {card.label}
                            </Typography>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: card.color, fontFamily: PP }}>
                                {card.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Calculation breakdown — same box, same shape as the commission page */}
                <Box sx={{ background: '#F8FAFC', borderRadius: '10px', padding: '12px 16px', mb: 2, border: '1px solid #E2E8F0' }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, fontFamily: PP }}>
                        RFC Calculation
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontFamily: PP }}>
                                Type: <strong>{cd.commPayType || (asternBorne ? 'RefA' : 'RefC')}</strong>
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontFamily: PP }}>
                                Payment To: <strong>{sd.referrerName || '—'}</strong>
                            </Typography>
                        </Box>
                        <Box sx={{ borderTop: '1px dashed #E2E8F0', pt: 0.5, mt: 0.5 }}>
                            <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontFamily: PP }}>
                                Final Price: <strong>RM {(sd.finalPrice || 0).toLocaleString()}</strong>
                                {' × '} Rate: <strong>{ratePct}%</strong>
                                {' = '} <strong style={{ color: '#1A8CFF' }}>{rm(fee)}</strong>
                            </Typography>
                        </Box>
                        {sd.originalPrice != null && sd.originalPrice !== sd.finalPrice && (
                            <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: PP }}>
                                Ori Price RM {sd.originalPrice.toLocaleString()}
                                {sd.discountByAstern ? ` − Astern Disc RM ${sd.discountByAstern.toLocaleString()}` : ''}
                                {sd.discountByAgent ? ` − Agent Disc RM ${sd.discountByAgent.toLocaleString()}` : ''}
                                {sd.discountByUpline ? ` − Upline Disc RM ${sd.discountByUpline.toLocaleString()}` : ''}
                                {sd.discountByTeam ? ` − Team Disc RM ${sd.discountByTeam.toLocaleString()}` : ''}
                                {' = '}Final RM {sd.finalPrice.toLocaleString()}
                            </Typography>
                        )}
                        {/* The add-on exclusion is the thing people query about a referral fee,
                            so it is written on the page rather than left to be rediscovered. */}
                        <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: PP }}>
                            {sd.addOnPrice
                                ? `Add-ons RM ${Number(sd.addOnPrice).toLocaleString()} are EXCLUDED — a referral fee is worked out on the system price only.`
                                : 'Worked out on the system price only; add-ons never enter a referral fee.'}
                        </Typography>
                    </Box>
                </Box>

                {/* Sales Information */}
                <SectionTitle>Sales Information</SectionTitle>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                    <TextField label="Agent Name" size="small" fullWidth disabled value={sd.agentName ?? ''} InputProps={inputSx} />
                    <TextField label="Co-Agent (Offline)" size="small" fullWidth disabled value={sd.coAgentName ?? ''} InputProps={inputSx} />
                    <TextField label="Customer Name" size="small" fullWidth disabled value={sd.customerName ?? ''} InputProps={inputSx} />
                    <TextField label="Category" size="small" fullWidth disabled value={sd.productCategory ?? ''} InputProps={inputSx} />
                    <TextField label="Energy" size="small" fullWidth disabled value={sd.energy != null ? `${sd.energy} kWp` : ''} InputProps={inputSx} />
                    <TextField label="Sale Status" size="small" fullWidth disabled value={sd.status ?? ''} InputProps={inputSx} />
                    <DatePicker label="Deposit Date" format="DD/MM/YYYY" disabled
                        value={sd.depositOn ? dayjs(sd.depositOn) : null} onChange={() => {}}
                        slotProps={{ textField: { size: 'small', fullWidth: true, InputProps: inputSx } }} />
                    <TextField label="Final Price" size="small" fullWidth disabled value={sd.finalPrice != null ? rm(sd.finalPrice) : ''} InputProps={inputSx} />
                    <TextField label="Customer Paid" size="small" fullWidth disabled
                        value={`${rm(sd.paidTotal)} of ${rm(sd.effectiveTotal)} (${Number(sd.paidPct ?? 0)}%)`} InputProps={inputSx} />
                </Box>

                {/* Referrer — the half of this page the commission page has no equivalent of */}
                <SectionTitle right={
                    sd.referrerIsCustomer
                        ? <Chip size="small" label="Also a customer" sx={{ height: 20, fontSize: '0.66rem', fontWeight: 600, backgroundColor: '#F0FDF4', color: '#166534' }} />
                        : null
                }>
                    Referrer
                </SectionTitle>
                {noBank && (
                    <Alert severity="warning" sx={{ mb: 1.5, fontFamily: PP, fontSize: '0.8rem', borderRadius: '10px' }}>
                        No bank details on file for this referrer — the fee cannot be paid out until they are keyed on the referrer's profile.
                    </Alert>
                )}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                    <TextField label="Referrer Name" size="small" fullWidth disabled value={sd.referrerName ?? ''} InputProps={inputSx} />
                    <TextField label="Phone" size="small" fullWidth disabled value={sd.referrerPhone ?? ''} InputProps={inputSx} />
                    <TextField label="Email" size="small" fullWidth disabled value={sd.referrerEmail ?? ''} InputProps={inputSx} />
                    <TextField label="Referral Rate" size="small" fullWidth disabled value={sd.referralRatePct != null ? `${Number(sd.referralRatePct)}%` : ''} InputProps={inputSx} />
                    <TextField label="Bank" size="small" fullWidth disabled value={sd.referrerBankName ?? ''} InputProps={inputSx} />
                    <TextField label="Account No" size="small" fullWidth disabled value={sd.referrerBankAccountNumber ?? ''} InputProps={inputSx} />
                    <TextField label="Beneficiary Name" size="small" fullWidth disabled value={sd.referrerBankBeneficiaryName ?? ''} InputProps={inputSx} />
                    <TextField label="Customer Of (most recent)" size="small" fullWidth disabled
                        value={[sd.referrerCustomerAgentName, sd.referrerCustomerCoAgentName].filter(Boolean).join(' + ')} InputProps={inputSx} />
                </Box>

                {/* A referrer who bought from us before may have bought from a DIFFERENT agent
                    each time, and which of them it was is exactly what gets asked when a
                    referral turns up. The single "Customer Of" field above can only carry the
                    most recent, so the whole history sits here.
                    (user 2026-10-14: "if the referral is customer, can noe their previous
                    sales is by who? i wanna have this record aso") */}
                {ownSales.length > 0 && (
                    <>
                        <SectionTitle right={
                            <Chip size="small" label={`${ownSales.length} purchase${ownSales.length === 1 ? '' : 's'} · ${rm(ownSalesTotal)}`}
                                sx={{ height: 20, fontSize: '0.66rem', fontWeight: 600, backgroundColor: '#F0FDF4', color: '#166534' }} />
                        }>
                            Their own purchases — sold by
                        </SectionTitle>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {['Sale', 'Deposit Date', 'Sold By', 'Category', 'Energy', 'Final Price', 'Status'].map(h => (
                                            <TableCell key={h} sx={{
                                                backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase',
                                                fontSize: '0.66rem', fontWeight: 600, letterSpacing: '0.4px', color: '#64748B',
                                                fontFamily: PP, padding: '8px 10px', whiteSpace: 'nowrap',
                                            }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {ownSales.map(s => (
                                        <TableRow key={s.salesId} hover>
                                            <TableCell sx={{ ...tdSx, fontWeight: 600, color: '#1A8CFF' }}>#{s.salesId}</TableCell>
                                            <TableCell sx={tdSx}>{s.depositOn ? dayjs(s.depositOn).format('DD/MM/YYYY') : '—'}</TableCell>
                                            <TableCell sx={{ ...tdSx, fontWeight: 600, color: '#0F172A' }}>
                                                {[s.agentName, s.coAgentName].filter(Boolean).join(' + ') || '—'}
                                            </TableCell>
                                            <TableCell sx={tdSx}>{s.productCategory || '—'}</TableCell>
                                            <TableCell sx={tdSx}>{s.energy != null ? `${s.energy} kWp` : '—'}</TableCell>
                                            <TableCell sx={tdSx}>{rm(s.finalPrice)}</TableCell>
                                            <TableCell sx={tdSx}>{s.status || '—'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </>
                )}

                {/* Payout — same two-column shape as the commission page's 1st/2nd Pay, but
                    read-only: this money is paid by issuing a referral slip, not by keying a
                    payment here. */}
                <SectionTitle right={
                    <Chip label={payStatus} size="small"
                        sx={{
                            height: 20, fontSize: '0.66rem', fontWeight: 600, fontFamily: PP,
                            backgroundColor: slipStatus === 'Approved' ? '#ECFDF5' : slipStatus === 'Pending' ? '#EFF6FF' : '#F1F5F9',
                            color: slipStatus === 'Approved' ? '#065F46' : slipStatus === 'Pending' ? '#0E8FD1' : '#475569',
                        }} />
                }>
                    Payout
                </SectionTitle>
                <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontStyle: 'italic', mb: 0.5, fontFamily: PP }}>
                    {asternBorne
                        ? 'Astern is the payer — key what they paid and it is recorded here. The fee becomes payable once the customer has paid 60% of the sale, and the whole fee is paid at once. Saving writes an approved referral slip, the same record the Slips page generates.'
                        : "The selling agent bears this fee: it came out of their PSC and rides their normal commission slip, which is where its payment is recorded."}
                </Typography>
                {saveErr && <Alert severity="error" sx={{ mb: 1.5, fontFamily: PP, fontSize: '0.8rem', borderRadius: '10px' }}>{saveErr}</Alert>}
                {saveOk && <Alert severity="success" sx={{ mb: 1.5, fontFamily: PP, fontSize: '0.8rem', borderRadius: '10px' }}>{saveOk}</Alert>}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <TextField label="Payout" size="small" fullWidth type="number"
                        disabled={!canRecord} value={payAmount}
                        onChange={(e) => setPayAmount(e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        placeholder={`Full fee ${rm(fee)}`}
                        InputProps={inputSx} />
                    <DatePicker label="Payout Date" format="DD/MM/YYYY" disabled={!canRecord}
                        value={payDate} onChange={(v) => setPayDate(v)}
                        slotProps={{ textField: { size: 'small', fullWidth: true, InputProps: inputSx } }} />
                </Box>
                {canRecord && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button variant="contained" size="small" disabled={saving} onClick={savePayout}
                            sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px', background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', boxShadow: 'none' }}>
                            {saving ? 'Saving…' : 'Save Payout'}
                        </Button>
                        {/* One click for the normal case: the whole fee, today. */}
                        <Button size="small" onClick={() => { setPayAmount(String(fee)); setPayDate(dayjs()); }}
                            sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }}>
                            Fill full fee, today
                        </Button>
                        <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: PP }}>
                            Saved as an approved referral slip — re-saving corrects it rather than paying twice.
                        </Typography>
                    </Box>
                )}
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {asternBorne && slipId && (
                        <Button size="small" variant="outlined" onClick={() => navigate('/accounting/slips')}
                            sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px' }}>
                            Referral slip #{slipId} ({slipStatus})
                        </Button>
                    )}
                    {asternBorne && !slipId && (
                        <Button size="small" variant="outlined" onClick={() => navigate('/accounting/slips')}
                            sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px' }}>
                            Generate on the Slips page
                        </Button>
                    )}
                    {!asternBorne && Number(row.commProgressId) > 0 && (
                        <Button size="small" variant="outlined" onClick={() => navigate(`/accounting/commission/${row.commProgressId}`)}
                            sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px' }}>
                            Open the commission record
                        </Button>
                    )}
                    {sd.referrerUserId > 0 && (
                        <Button size="small" variant="outlined" onClick={() => navigate(`/reports/referral/${sd.referrerUserId}`)}
                            sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px', color: '#64748B', borderColor: '#CBD5E1' }}>
                            All referrals by {sd.referrerName}
                        </Button>
                    )}
                </Box>

                {/* Remarks & log — the sale's history, read-only */}
                <SectionTitle>Remarks</SectionTitle>
                <LogViewer salesId={parseInt(salesId, 10)} readOnly />
            </Paper>
        </Box>
    );
}
